import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import Button from "./../../Components/common/Button";
import { useNavigate } from "react-router-dom";
import { logInUserInfo } from "../../Actions/Users";
import { clearSession, fetchAccounts, fetchPrivileges } from "./LoginServices";
import { ServerInfo } from "../../Actions/serverSetting";
import { getPrivileges } from "../../Actions/Privileges";
import { checkedDevelopmentType } from "../../Helpers";
import instance from "../../axios";
import { notifyError, notifySuccess } from "../../Utils/CustomNotifcations";
import { useDispatch } from "react-redux";
const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
export default function OTPForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const OTP_LENGTH = 6;
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [resendTimer, setResendTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState("");
  const inputsRef = useRef([]);
  useEffect(() => {
    const expiry = localStorage.getItem("otpExpiry");
    if (expiry) {
      const remaining = Math.max(
        0,
        Math.floor((parseInt(expiry, 10) - Date.now()) / 1000)
      );
      if (remaining > 0) {
        setResendTimer(remaining);
      } else {
        localStorage.removeItem("otpExpiry");
      }
    }
  }, []);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [resendTimer]);

  const handleChange = (value, index) => {
    if (/^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value.slice(-1);
      setOtp(newOtp);

      if (value && index < OTP_LENGTH - 1) {
        inputsRef.current[index + 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("Text").trim();
    if (!/^\d+$/.test(pastedData)) return;

    const otpArray = pastedData.split("").slice(0, OTP_LENGTH);
    setOtp([...otpArray, ...new Array(OTP_LENGTH - otpArray.length).fill("")]);

    const lastIndex = otpArray.length - 1;
    if (lastIndex >= 0) inputsRef.current[lastIndex]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fetchUrl = checkedDevelopmentType();
    const app = `3b8d4deb84c0d9b65070c872e2f73626f23d8dc8789a7a05`;
    const otpValue = otp.join("");

    try {
      setLoading(true);
      const response = await instance.post(`/api/session/verify-otp`,
        new URLSearchParams({ otp: otpValue, app,fetchUrl })
      );

      await handleLoginService({
        res: response?.data,
        dispatch,
        navigate,
        setLoading,
        setErrorMessage: setLoginErrorMessage,
        goToAccount: () => navigate("/users"),
      });
        localStorage.removeItem("otpExpiry");
    } catch (error) {
     setLoading(false);

  const serverMsg = error?.message || error?.message;

  if (serverMsg?.toLowerCase().includes("invalid") || serverMsg?.toLowerCase().includes("expired")) {
      dispatch(notifyError("Incorrect or Expired OTP" || "Something went wrong"));
  } else {
      dispatch(notifyError(serverMsg || "Something went wrong"));
  }
    }
  };

  const handleResend = async () => {
    try {
      const fetchUrl = checkedDevelopmentType();
      const app = `3b8d4deb84c0d9b65070c872e2f73626f23d8dc8789a7a05`;

      const response = await instance.post(`/api/session/resend-otp`,
        new URLSearchParams({ app,fetchUrl })
      );

      dispatch(notifySuccess(response?.data || "OTP resent successfully"));

      const newExpiry = Date.now() + 300 * 1000;
      localStorage.setItem("otpExpiry", newExpiry.toString());
      setResendTimer(300);
      setOtp(Array(OTP_LENGTH).fill(""));
    } catch (error) {
      dispatch(notifyError(error?.message || "Resend OTP failed"));
    }
  };

  const backToLogin = () => {
    navigate("/login");
  };

  const isOtpComplete = otp.every((digit) => digit !== "");

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        maxWidth: 400,
        mx: "auto",
        mt: 6,
        p: 4,
        borderRadius: 3,
        boxShadow: 3,
        textAlign: "center",
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Enter OTP
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Please enter the 6-digit OTP sent to your email.
      </Typography>

      <Box display="flex" justifyContent="center" gap={1} mb={3}>
        {otp.map((digit, index) => (
          <TextField
            key={index}
            inputRef={(el) => (inputsRef.current[index] = el)}
            value={digit}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            inputProps={{
              maxLength: 1,
              style: { textAlign: "center", fontSize: "1.5rem" },
            }}
            sx={{ width: 60 }}
          />
        ))}
      </Box>

      <Button
        fullWidth
        type="submit"
        variant="contained"
        sx={{ mb: 2, borderRadius: 2, py: 1.2 }}
        disabled={!isOtpComplete || loading}
      >
        {loading ? "Verifying..." : "Verify OTP"}
      </Button>

      {loginErrorMessage && (
        <Typography color="error" variant="body2" mb={2}>
          {loginErrorMessage}
        </Typography>
      )}

      <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
        <Tooltip title="Resend OTP">
          <span>
            <IconButton
              onClick={handleResend}
              disabled={resendTimer > 0}
              color="primary"
            >
              <ReplayIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="body2" color="text.secondary">
{resendTimer > 0
  ? `Resend available in ${formatTime(resendTimer)}`
  : "You can resend OTP now"}
        </Typography>
      </Box>

      <Button onClick={backToLogin} sx={{ mt: 2 }}>
        Back to Login
      </Button>
    </Box>
  );
}

export const handleLoginService = async ({
  res,
  dispatch,
  navigate,
  setLoading,
  setErrorMessage,
  goToAccount,
}) => {
  try {
    const userInfo = res?.data;
    if (!userInfo) throw new Error("Invalid login response");

    dispatch(logInUserInfo(userInfo));
    localStorage.setItem("userInfo", JSON.stringify(userInfo));
    localStorage.setItem("adminToken", "");

    const res1 = await fetchAccounts(userInfo?.accountId);
    if (!(res1.status === "success" && res1.data?.length)) {
      throw new Error("Account Data Not Found");
    }

    dispatch(ServerInfo(res1.data[0]));

    const res2 = await fetchPrivileges(userInfo);
    if (res2.status === "success") {
      const privileges = res2.data;
      dispatch(getPrivileges(privileges));

      const privilegeKeys = privileges.map((item) => item.key);
      localStorage.setItem("privileges", JSON.stringify(privilegeKeys));

      if (privilegeKeys.length) {
        goToAccount();
      } else {
        throw new Error("Account Data Not Found");
      }
    } else if (res2?.statusCode === 440) {
      window.location.replace("/login");
    } else {
      throw new Error(res2?.message || "Failed to fetch privileges");
    }
  } catch (e) {
    setErrorMessage(e.message || "Something went wrong");
    dispatch(notifyError(e.message || "Something went wrong"));
    clearSession(dispatch, navigate);
  } finally {
    setLoading(false);
  }
};
